/**
 * S115 design tokens.
 *
 * The palette stays in the original spirit of the site — near black canvas,
 * bright type, hairline borders — but the values are re-tuned into a proper
 * scale (surfaces, muted text, one warm signal accent) so every screen can be
 * built from the same vocabulary.
 *
 * Legacy keys used by `src/lib/UIBookBrd` are kept at the bottom so older
 * components keep rendering while they get migrated.
 */

export const DARK_THEME = {
    isDark: true,

    /* ---------------------------------------------------------------- canvas */
    backgroundColor: '#08080A',
    surface: '#0E0E12',
    surfaceRaised: '#14141A',
    surfaceHover: '#1B1B22',
    surfaceSunken: '#050506',
    overlay: 'rgba(4, 4, 6, 0.72)',

    /* ------------------------------------------------------------------ text */
    color: '#F4F4F6',
    colorMuted: '#9C9CA8',
    colorFaint: '#66666F',
    colorInverted: '#08080A',

    /* ----------------------------------------------------------------- lines */
    line: 'rgba(255, 255, 255, 0.10)',
    lineStrong: 'rgba(255, 255, 255, 0.22)',
    lineFaint: 'rgba(255, 255, 255, 0.05)',

    /*
     * Accent — deliberately monochrome. The site reads as graphite and steel,
     * so the accent carries emphasis through brightness, not hue.
     */
    accent: '#B4BAC4',
    accentHover: '#D6DAE1',
    accentSoft: 'rgba(180, 186, 196, 0.12)',
    accentLine: 'rgba(180, 186, 196, 0.42)',
    accentContrast: '#0A0A0B',

    /* Reserved for real feedback (sent / failed), never for decoration. */
    success: '#35C46A',
    danger: '#FF4F4F',

    /* ------------------------------------------------------------- structure */
    radius: {
        xs: '6px',
        sm: '10px',
        md: '14px',
        lg: '20px',
        xl: '28px',
        pill: '999px'
    },
    shadow: {
        sm: '0 2px 8px rgba(0, 0, 0, 0.4)',
        md: '0 12px 32px rgba(0, 0, 0, 0.45)',
        lg: '0 32px 80px rgba(0, 0, 0, 0.6)',
        accent: '0 12px 32px rgba(180, 186, 196, 0.18)'
    },
    ease: {
        out: 'cubic-bezier(0.16, 1, 0.3, 1)',
        inOut: 'cubic-bezier(0.65, 0, 0.35, 1)'
    },
    header: {
        height: 72,
        heightMobile: 62
    },
    font: {
        body: `-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
        display: `-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
        logo: `IconicS115, -apple-system, BlinkMacSystemFont, sans-serif`,
        mono: `'SFMono-Regular', ui-monospace, Menlo, Monaco, Consolas, monospace`
    },

    /* ------------------------------------------------- legacy UIBookBrd keys */
    OfferListBackgroundColor: 'rgba(255, 255, 255, 0.06)',
    selectTagsBackgroundColor: '#2A2A31',
    toolTipColor: '#1B1B22',
    focusedInputBorderColor: '#B4BAC4',
    optionHoverColor: '#1B1B22',
    calendarSelectedDate: '#000000',
    calendarFillDates: '#4F4F4F',
    placeholderColor: '#66666F',
    borderColor: 'rgba(255, 255, 255, 0.14)',
    borderColorLight: 'rgba(255, 255, 255, 0.06)',
    separatorColor: 'rgba(255, 255, 255, 0.14)',
    buttonBackgroundColor: '#B4BAC4',
    typeOfSearchHover: 'rgba(0, 0, 0, 0.5)',
    colorBlue: '#4BA3F5',
    disabledFontColor: '#66666F',
    isCheckedSwitchGreen: '#35C46A',
    ratingStarColor: '#D79B1F',
    circleInfoColor: '#4BA3F5',
    successMessageBackgroundColor: 'rgba(53, 196, 106, 0.9)',
    errorMessageBackgroundColor: 'rgba(255, 79, 79, 0.9)',
    errorMessageIconColor: '#FF4F4F',
    flameIconColor: '#B4BAC4',
    warningBackgroundColor: 'rgba(241, 196, 105, 0.9)',
    brdExclusiveColor: '#4BA3F5',
    missingRatingColor: '#575757'
}

export type AppTheme = typeof DARK_THEME

export default DARK_THEME
